// RTC 连接封装。两种连接路径:
//
//   connectDebug   — POST /debug/offer 走 HTTP SDP 交换(本机 smoke)
//   connectCloud   — wss /ws/user 走云信令 SDP/ICE 双向(P0 运营接管)
//
// 共享:DataChannel 'input' 作为输入 + handoff 控制通道(offerer 侧建,
// agent OnDataChannel 接收);返回相同的 ConnectHandle,让 main.ts 两条
// 路径走同一套 current 接管 / detach 逻辑。

import {
  SignalingClient,
  type IceCandidatePayload,
} from '@no-vnc-web/rtc';
import type { Send, WireEnvelope } from '@no-vnc-web/input';

export interface ConnectParams {
  endpoint: string;
  onTrack: (stream: MediaStream) => void;
  onState: (state: RTCPeerConnectionState) => void;
  onLog: (level: 'info' | 'ok' | 'warn' | 'err', msg: string) => void;
  /** 入站 DataChannel 消息(JSON envelope)解码后交给调用方。 */
  onMessage?: (env: WireEnvelope) => void;
}

export interface CloudConnectParams {
  wsURL: string;
  token: string;
  /**
   * signaling HTTP base(用于自动拉 TURN credentials)。
   * 形如 `https://signaling.novnc.aiform.com`,空 = 不拉 TURN(只用 iceServers 或 host-only)。
   */
  httpBase?: string;
  /**
   * 显式 TURN/STUN servers。优先级最高:非 undefined → 直接用,不再调 httpBase 拉。
   * 留空数组 = 强制 host-only(测试 / dev)。
   */
  iceServers?: RTCIceServer[];
  onTrack: (stream: MediaStream) => void;
  onState: (state: RTCPeerConnectionState) => void;
  onLog: (level: 'info' | 'ok' | 'warn' | 'err', msg: string) => void;
  onMessage?: (env: WireEnvelope) => void;
}

export interface ConnectHandle {
  pc: RTCPeerConnection;
  dc: RTCDataChannel;
  /** 便捷 send:JSON stringify 后发到 DataChannel。 */
  send: Send;
  close: () => void;
}

interface AgentAnswer {
  sdp: string;
}

interface AgentError {
  error: string;
}

/** setupDC 装配 DataChannel + send queue + stats timer,debug/cloud 共享。 */
function setupDC(
  dc: RTCDataChannel,
  onLog: ConnectParams['onLog'],
  onMessage?: (env: WireEnvelope) => void,
): { send: Send; cleanup: () => void } {
  const sendQueue: WireEnvelope[] = [];
  let dcReady = false;
  let sentCount = 0;
  let queuedCount = 0;
  let lastStatSent = -1;
  let lastStatQueued = -1;
  const statTimer = setInterval(() => {
    if (sentCount === lastStatSent && queuedCount === lastStatQueued) return;
    onLog('info', `dc sent=${sentCount} queued=${queuedCount} ready=${dcReady}`);
    lastStatSent = sentCount;
    lastStatQueued = queuedCount;
  }, 2000);
  dc.addEventListener('open', () => {
    dcReady = true;
    onLog('ok', `dc open (${sendQueue.length} queued)`);
    while (sendQueue.length > 0) {
      const env = sendQueue.shift();
      if (env) {
        dc.send(JSON.stringify(env));
        sentCount += 1;
      }
    }
  });
  dc.addEventListener('close', () => {
    dcReady = false;
    onLog('info', 'dc closed');
  });
  dc.addEventListener('error', (ev) => {
    onLog('err', `dc error: ${describeError(ev)}`);
  });
  if (onMessage) {
    dc.addEventListener('message', (ev) => {
      if (typeof ev.data !== 'string') return;
      try {
        const env = JSON.parse(ev.data) as WireEnvelope;
        onMessage(env);
      } catch {
        onLog('warn', 'dc recv: invalid JSON');
      }
    });
  }

  const send: Send = (env: WireEnvelope) => {
    if (!dcReady) {
      sendQueue.push(env);
      queuedCount += 1;
      return;
    }
    try {
      dc.send(JSON.stringify(env));
      sentCount += 1;
    } catch (e) {
      onLog('err', `dc send: ${toMsg(e)}`);
    }
  };

  return {
    send,
    cleanup: () => {
      clearInterval(statTimer);
    },
  };
}

export async function connectDebug(params: ConnectParams): Promise<ConnectHandle> {
  const { endpoint, onTrack, onState, onLog, onMessage } = params;

  const pc = new RTCPeerConnection({ iceServers: [] });
  pc.addTransceiver('video', { direction: 'recvonly' });

  // Offerer 侧建 DataChannel。agent 在 OnDataChannel 里收。
  const dc = pc.createDataChannel('input', { ordered: true });
  const { send, cleanup: cleanupDC } = setupDC(dc, onLog, onMessage);

  pc.onconnectionstatechange = () => {
    onLog('info', `pc state: ${pc.connectionState}`);
    onState(pc.connectionState);
  };
  pc.oniceconnectionstatechange = () => {
    onLog('info', `ice state: ${pc.iceConnectionState}`);
  };
  pc.ontrack = (ev) => {
    onLog('ok', `track: kind=${ev.track.kind} streams=${ev.streams.length}`);
    if (ev.streams[0]) onTrack(ev.streams[0]);
  };

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await waitGatheringComplete(pc);

  const local = pc.localDescription;
  if (!local) throw new Error('localDescription is null after gathering');
  onLog('info', `offer ready (${local.sdp.length} bytes), POST ${endpoint}`);

  const resp = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sdp: local.sdp }),
  });

  if (!resp.ok) {
    const text = await resp.text();
    let msg = text;
    try {
      const err = JSON.parse(text) as AgentError;
      if (err.error) msg = err.error;
    } catch {
      /* ignore */
    }
    throw new Error(`agent ${resp.status}: ${msg}`);
  }

  const answer = (await resp.json()) as AgentAnswer;
  if (!answer.sdp) throw new Error('agent returned empty sdp');
  onLog('ok', `answer received (${answer.sdp.length} bytes)`);

  await pc.setRemoteDescription({ type: 'answer', sdp: answer.sdp });

  return {
    pc,
    dc,
    send,
    close: () => {
      onLog('info', 'pc closed');
      cleanupDC();
      try {
        dc.close();
      } catch {
        /* ignore */
      }
      pc.close();
    },
  };
}

/**
 * connectCloud 走 WebSocket 信令(P0 运营接管路径)。
 *
 * 跟 connectDebug 的关键差异:
 *   - SDP 不通过 HTTP 一次性交换,而是通过 WS 双向(offer/answer/ICE)
 *   - 不立刻 createOffer,等 SignalingClient 的 onPeerJoined(agent 到位)
 *     才发 offer — 否则 agent 还没连进 hub,发了 offer 会被 hub 丢弃
 *   - 仍然 offerer 侧 createDataChannel,跟 debug 完全一致(agent 端处理逻辑共用)
 *   - iceServers 由 caller 注入(TURN credentials 由 GET /api/v1/turn-credentials
 *     拉,见 #55;不传 = 空数组,弱网会失败)
 *
 * await 返回时机:WS open 后立即返回 handle(此时 PC 还未 connected,caller
 * 注册的 onState/onTrack 在后续回调里触发)。这跟 connectDebug 不同(后者
 * await 时 SDP 已交换完);但 caller 不需要区分,handle 接口完全一致。
 */
export async function connectCloud(params: CloudConnectParams): Promise<ConnectHandle> {
  const { wsURL, token, httpBase, iceServers, onTrack, onState, onLog, onMessage } = params;

  // iceServers 优先级:显式传 > 调 httpBase 拉 > 空数组(host-only)
  let effectiveIceServers: RTCIceServer[] = [];
  if (iceServers !== undefined) {
    effectiveIceServers = iceServers;
  } else if (httpBase) {
    effectiveIceServers = await fetchTurnCredentials(httpBase, token, onLog);
  }

  const pc = new RTCPeerConnection({ iceServers: effectiveIceServers });
  pc.addTransceiver('video', { direction: 'recvonly' });
  const dc = pc.createDataChannel('input', { ordered: true });
  const { send, cleanup: cleanupDC } = setupDC(dc, onLog, onMessage);

  pc.onconnectionstatechange = () => {
    onLog('info', `cloud pc state: ${pc.connectionState}`);
    onState(pc.connectionState);
  };
  pc.oniceconnectionstatechange = () => {
    onLog('info', `cloud ice state: ${pc.iceConnectionState}`);
  };
  pc.ontrack = (ev) => {
    onLog('ok', `cloud track: kind=${ev.track.kind} streams=${ev.streams.length}`);
    if (ev.streams[0]) onTrack(ev.streams[0]);
  };

  const sig = new SignalingClient(
    { url: wsURL, token },
    {
      onOpen: () => onLog('info', 'cloud: ws open'),
      onClose: (c, r) => onLog('info', `cloud: ws close ${c} ${r}`),
      onError: (err) => onLog('err', `cloud: server error ${err.code}: ${err.message}`),
      onPeerJoined: () => {
        onLog('ok', 'cloud: peer joined; sending offer');
        void negotiate();
      },
      onPeerLeft: () => onLog('warn', 'cloud: peer left'),
      onAnswer: async (sdp: string) => {
        await pc.setRemoteDescription({ type: 'answer', sdp });
        onLog('ok', 'cloud: answer applied');
      },
      onICE: async (cand: IceCandidatePayload) => {
        try {
          await pc.addIceCandidate({
            candidate: cand.candidate,
            sdpMid: cand.sdpMid ?? undefined,
            sdpMLineIndex: cand.sdpMLineIndex ?? undefined,
          });
        } catch (e) {
          onLog('warn', `addIceCandidate: ${toMsg(e)}`);
        }
      },
    },
  );

  pc.onicecandidate = (ev) => {
    if (!ev.candidate) return;
    try {
      sig.sendICE({
        candidate: ev.candidate.candidate,
        sdpMid: ev.candidate.sdpMid,
        sdpMLineIndex: ev.candidate.sdpMLineIndex,
      });
    } catch {
      /* sig not open yet — trickle ICE before WS ready is normal */
    }
  };

  async function negotiate(): Promise<void> {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    sig.sendOffer(offer.sdp ?? '');
    onLog('info', `cloud: offer sent (${(offer.sdp ?? '').length}B)`);
  }

  await sig.connect();

  return {
    pc,
    dc,
    send,
    close: () => {
      onLog('info', 'cloud: closing');
      cleanupDC();
      try {
        dc.close();
      } catch {
        /* ignore */
      }
      sig.close();
      pc.close();
    },
  };
}

/**
 * fetchTurnCredentials 拉 TURN 凭证,转成 RTCIceServer[]。
 *
 * - 用 `?t=<token>` 而不是 Authorization header,绕开 CORS preflight(GET 简单请求)
 * - signaling 端 token 必须含 session_id,否则 400;jwt 失效 → 401
 * - 任何失败 → 返回空数组 + log,让 caller 继续走 host-only(LAN 同网段可通)
 *
 * base 形如 `https://signaling.novnc.aiform.com`(不带 /,不带路径)。
 */
async function fetchTurnCredentials(
  base: string,
  token: string,
  onLog: ConnectParams['onLog'],
): Promise<RTCIceServer[]> {
  try {
    const url = `${base.replace(/\/$/, '')}/api/v1/turn-credentials?t=${encodeURIComponent(token)}`;
    const resp = await fetch(url, { method: 'GET' });
    if (!resp.ok) {
      onLog('warn', `cloud: turn-credentials ${resp.status}; continuing host-only`);
      return [];
    }
    const creds = (await resp.json()) as {
      username: string;
      credential: string;
      urls: string[];
    };
    if (!Array.isArray(creds.urls) || creds.urls.length === 0) {
      onLog('warn', 'cloud: turn-credentials returned no URLs');
      return [];
    }
    onLog('ok', `cloud: turn ready (${creds.urls.length} URL(s))`);
    return [
      {
        urls: creds.urls,
        username: creds.username,
        credential: creds.credential,
      },
    ];
  } catch (e) {
    onLog('warn', `cloud: turn-credentials fetch failed: ${toMsg(e)}; continuing host-only`);
    return [];
  }
}

function waitGatheringComplete(pc: RTCPeerConnection, timeoutMs = 3000): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const to = setTimeout(() => {
      pc.removeEventListener('icegatheringstatechange', onChange);
      resolve();
    }, timeoutMs);
    const onChange = () => {
      if (pc.iceGatheringState === 'complete') {
        clearTimeout(to);
        pc.removeEventListener('icegatheringstatechange', onChange);
        resolve();
      }
    };
    pc.addEventListener('icegatheringstatechange', onChange);
  });
}

function toMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}

function describeError(ev: Event): string {
  const anyEv = ev as unknown as { error?: { message?: string } };
  return anyEv?.error?.message ?? 'unspecified';
}
